/*------------------------------------------------------------------
Internal imports
------------------------------------------------------------------*/

import { $, $$, debounce } from '@/utilities/helpers';
import { initDoughnutChart } from '@/charts/doughnut';
import { initBarChart } from '@/charts/bar';
import { initLineChart } from '@/charts/line';
import { default_choices_options, default_multi_choices_options } from '@/utilities/constants';

import { Dialog } from '@/components/dialogs';

/*------------------------------------------------------------------
External imports
------------------------------------------------------------------*/

import { up } from 'slide-element';
import Choices from 'choices.js';

const DEBUG = window.location.href.includes('smart.test');

/*------------------------------------------------------------------
Elements
------------------------------------------------------------------*/


let isScrolling = false;
let isInitialPageLoad = true;
let shouldFetchOnLoad = false;
let hasFetchedOnLoad = false;

// MIPS|BONDS|SHARES pages should not bypass the initial page load fetch
if (window.compare_type === 'mips' || window.compare_type === 'bonds' || window.compare_type === 'shares') shouldFetchOnLoad = true;

let $results = $('.js-results');
let $filterSet = $$('.js-filter-set');
let $compareBar = $('.js-compare-bar');
let $resultsCount = $('.js-results-count');
let $loadMoreResults = $('.js-load-more-results');
let $scrollToCompare = $$('.js-scroll-to-compare');
let $clearCompare = $$('.js-clear-compare');
let $scrollToSearch = $$('.js-scroll-to-search');
let $toggleFilterMode = $$('.js-toggle-filter-mode');
let $fundTypeDescription = $$('.js-fund-type-description');
let $closeFundTypeDescription = $$('.js-close-fund-type-description');
let $triggerFundTypeDescription = $$('.js-trigger-fund-type-description');
let $loadingBar = $('.loading-bar');

// Promote the loading bar into the browser's top layer (same place a
// <dialog> lives) so it can still render above an open filter dialog - a
// regular fixed-position element always renders behind an open modal
// <dialog>, regardless of z-index. The top layer stacks by recency (most
// recently promoted = topmost), so this has to be re-run right before
// each fetch rather than once at page load - otherwise a dialog opened
// afterwards would always end up above it. Visibility itself is still
// driven by the .fetching/.fetched classes; this only affects stacking.
function bringLoadingBarToFront() {
    if (!$loadingBar?.showPopover) return;
    if ($loadingBar.matches(':popover-open')) $loadingBar.hidePopover();
    $loadingBar.showPopover();
}


let industry_select = initSelect($('#industry'), 'industry');
let providers_select = initSelect($('#providers'), 'providers');
let minimum_investment_select = initSelect($('#minimum-investment'), 'minInvest');
let status_select = initSelect($('#status'), 'status', () => statusHasChanged = true);
let maximum_fees_select = initSelect($('#maximum-fees'), 'maxFees', () => maxFeesHasChanged = true);
let total_value_select = initSelect($('#total-value'), 'minTotalValue', () => minTotalValueHasChanged = true);
let minimum_past_return_select = initSelect($('#minimum-past-return'), 'minPastReturn', () => minPastReturnHasChanged = true);
let minimum_risk_indicator_select = initSelect($('#minimum-risk-indicator'), 'riskIndicatorMin', () => riskIndicatorMinHasChanged = true);
let maximum_risk_indicator_select = initSelect($('#maximum-risk-indicator'), 'riskIndicatorMax', () => riskIndicatorMaxHasChanged = true);

let statusHasChanged = false;
let maxFeesHasChanged = false;
let isSyncingFromPopstate = false;
let minTotalValueHasChanged = false;
let minPastReturnHasChanged = false;
let changeSearchTypeHasChanged = false;
let riskIndicatorMinHasChanged = false;
let riskIndicatorMaxHasChanged = false;

window.stored_data = null;

if (localStorage.getItem('stored_data')) {
    window.stored_data = JSON.parse(localStorage.getItem('stored_data'));
}

let abort_controller = new AbortController();
let signal = abort_controller.signal;

let current_page = undefined;

const storedCompare = localStorage.getItem(`compare-${window.compare_type}`);
window.compare_items = storedCompare ? JSON.parse(storedCompare) : [];


/**
 * Get the current filters from the URL
 * @returns {object}
 */
function getCurrentFilters() {
    return Object.fromEntries(new URLSearchParams(window.location.search));
}

/**
 * Check if two filter objects match
 * @param {object} storedFilters 
 * @param {object} currentFilters 
 * @returns 
 */
function filtersMatch(storedFilters, currentFilters) {
    const keys = Object.keys(currentFilters);
    if (!storedFilters) return false;
    if (keys.length !== Object.keys(storedFilters).length) return false;
    return keys.every(key => storedFilters[key] === currentFilters[key]);
}


/**
* Update the selected choice for a given Choices.js instance
* @param {Choices} choice - The Choices.js instance to update
* @param {string} value - The value to set as selected
*/
function updateChoice(choice, value) {

    if (!choice) return;

    const exists = choice.passedElement.element.querySelector(`option[value="${value}"]`) !== null;

    choice.removeActiveItems();

    (exists) ? choice.setChoiceByValue(value) : choice.setChoiceByValue('');
}


/**
 * Sticky behavior for the compare bar:
 * If we are scrolling over the compare section, then we should hide the compare bar
 * @returns {void}
 */
let stickyCompareBar = () => {

    const $compareSection = $('.js-compare');

    if (!$compareSection || !$compareBar) return;

    const { top } = $compareSection.getBoundingClientRect();
    const threshold = top + window.scrollY - window.innerHeight;

    $compareBar.classList.toggle('hidden', window.scrollY > threshold);

};


/**
 * Handle scroll events with requestAnimationFrame for better performance
 * @returns {void}
 */
const scrollHandler = () => {
    if (!isScrolling && $compareBar) {
        window.requestAnimationFrame(() => {
            stickyCompareBar();
            isScrolling = false;
        });
        isScrolling = true;
    }
}

const prepareScrollableRegions = ($root = document) => {
    $root.querySelectorAll('.table-wrapper').forEach($wrapper => {
        $wrapper.classList.add('a11y-tab');
        $wrapper.setAttribute('tabindex', '0');
        $wrapper.setAttribute('role', 'region');
        if (!$wrapper.hasAttribute('aria-label') && !$wrapper.hasAttribute('aria-labelledby')) {
            $wrapper.setAttribute('aria-label', 'Fee breakdown table');
        }
    });

    $root.querySelectorAll('.card__tab[data-id="returns"] > .card__column--two-thirds:not(:empty)').forEach($column => {
        $column.classList.add('a11y-tab');
        $column.setAttribute('tabindex', '0');
        $column.setAttribute('role', 'region');
        if (!$column.hasAttribute('aria-label') && !$column.hasAttribute('aria-labelledby')) {
            $column.setAttribute('aria-label', 'Five-year returns chart and table');
        }
    });
}

/**
 * Render the fetched results into the results container
 * @param {object} data - The data returned from the API
 * @returns {void}
 */
const renderResults = ({ data = {}, message }) => {

    document.body.classList.add('post-initial-load');
    document.body.classList.add('fetching');

    setTimeout(() => document.body.classList.remove('fetched'), 500);

    if ($resultsCount && data.total !== undefined) {
        $resultsCount.innerText = data.total;
    }

    // Clear the previous results
    let html = '';

    // Build the results HTML
    if (data.results && data.results.length) {
        current_page = data.current_page;
        data.results.forEach(item => html += item);
    } else {
        current_page = undefined;
        html = /*html*/`<p class="text-center no-results-message">${message || 'There are presently no results that match your criteria, please try another search.'}</p>`;
    }

    // Show or hide the load more button
    if ($loadMoreResults && $loadMoreResults.parentNode) {
        if (data.has_more) {
            const remaining = data.total - (data.current_page * data.results_per_page);
            let nextBatch = Math.min(data.results_per_page, remaining);
            if (isNaN(nextBatch)) nextBatch = data.results_per_page;
            $loadMoreResults.innerHTML = `<b>Load ${nextBatch} more result${nextBatch === 1 ? '' : 's'}</b>`;
            $loadMoreResults.dataset.nextBatch = nextBatch;
            $loadMoreResults.parentNode.style.display = 'flex';
        } else {
            $loadMoreResults.parentNode.style.display = 'none';
        }
    }

    // Append or replace the results based on the current page
    if (current_page && current_page > 1) {
        $results.insertAdjacentHTML('beforeend', html);
    } else {
        $results.innerHTML = html;
    }

    prepareScrollableRegions($results);

    // Initialise any charts in the newly added results
    $$('.js-results .js-inline-doughnut:not(.initialised)').forEach($chart => {
        $chart.classList.add('initialised');
        initDoughnutChart($chart);
    });

    $$('.js-results .js-inline-horizontal-bar-chart:not(.initialised)').forEach($chart => {
        $chart.classList.add('initialised');
        initBarChart($chart);
    });

    $$('.js-results .js-inline-line-chart:not(.initialised)').forEach($chart => {
        $chart.classList.add('initialised');
        initLineChart($chart);
    });

    const dialogElements = $$('.js-dialog-trigger');
    dialogElements.forEach(element => new Dialog(element));

}


/**
 * Fetch results based on current filters
 * @returns {void}
 */
const fetchResults = (show_updating_message = true) => {
    if (DEBUG) console.log('fetchResults()')

    bringLoadingBarToFront();

    let apiUrl = $results?.dataset.apiUrl;

    if (!apiUrl) return;

    let url = new URL(apiUrl, window.location.origin);
    let params = new URLSearchParams(window.location.search);
    url.search = params.toString();

    // Abort any previous fetch request
    if (abort_controller) abort_controller.abort();
    abort_controller = new AbortController();
    signal = abort_controller.signal;

    if (DEBUG) console.log('fetchResults()');

    // Stop the initial page load from triggering a fetch if it is fresh with no filters
    if (isInitialPageLoad) {
        if (!url.search) {
            if (DEBUG) console.log('There are no search parameters, skipping fetch on initial load, Returning.');
            isInitialPageLoad = false;
        } else {
            shouldFetchOnLoad = true;
            document.body.classList.add('post-initial-load');
            document.body.classList.add('fetching');
        }
    } else {
        document.body.classList.add('post-initial-load');
        if (show_updating_message) {
            document.body.classList.add('fetching');
        }
    }

    if (isInitialPageLoad && window.stored_data) {
        const currentFilters = getCurrentFilters();
        if (filtersMatch(window.stored_data.filters, currentFilters)) {

            if (DEBUG) console.log('Cached data found, restoring', window.stored_data);

            if (show_updating_message) {
                document.body.classList.add('fetched');
            }
            setTimeout(() => document.body.classList.remove('fetching'), 250);
            setTimeout(() => renderResults({ data: window.stored_data }), 10);

            if (isInitialPageLoad) {
                isInitialPageLoad = false;
                if (localStorage.getItem('si_offset')) {
                    setTimeout(() => window.scrollTo(0, parseInt(localStorage.getItem('si_offset'))), 50);
                }
            }

            return;
        }
        else {
            // Filters don't match, clear stored_data
            localStorage.removeItem('stored_data');
            window.stored_data = null;
        }
    }

    // if url does not have sort then add a default sort of window.default_sort if present
    if (!url.searchParams.has('sort') && window.default_sort) {
        url.searchParams.set('sort', window.default_sort);
    }

    // This is for the case where the user has changed the fund status filter, but not the providers or industries, we still want to include the fund status in the URL and fetch results based on it, even if it is the same as the default value, as it indicates an intentional change by the user
    if (!statusHasChanged) {
        // Array of fund statuses
        let fundStatus = $('#status') ? [...$('#status').selectedOptions].map(o => o.value) : [];
        if (fundStatus.length) fundStatus.forEach(status => url.searchParams.append('fundStatus[]', status));
    }

    if (!hasFetchedOnLoad && !shouldFetchOnLoad) { shouldFetchOnLoad = true; return; }

    if (DEBUG) console.log('Fetching results from API:', url.toString());
    if (DEBUG) console.table(Object.fromEntries(url.searchParams.entries()));

    fetch(url.toString(), { signal })
        .then(response => response.json())
        .then(data => {
            if (show_updating_message) {
                document.body.classList.add('fetched');
            }
            setTimeout(() => document.body.classList.remove('fetching'), 250);

            hasFetchedOnLoad = true;

            // If we are on a search continuance
            if (window.location.search.includes('page=') && !window.location.search.includes('page=1')) {
                // Append the new results to the stored results
                window.stored_data = {
                    current_page: data.current_page,
                    total: data.total,
                    results: (stored_data?.results || []).concat(data.results),
                    results_per_page: data.results_per_page,
                    previous_page: data.previous_page,
                    filters: getCurrentFilters(),
                    has_more: data.has_more
                };
                localStorage.setItem('stored_data', JSON.stringify(window.stored_data));
            }

            // Else if we are on a fresh search, clear stored results
            else {
                // Store the results
                window.stored_data = {
                    current_page: data.current_page,
                    total: data.total,
                    results: data.results,
                    results_per_page: data.results_per_page,
                    previous_page: data.previous_page,
                    filters: getCurrentFilters(),
                    has_more: data.has_more
                };
                localStorage.setItem('stored_data', JSON.stringify(window.stored_data));
            }

            renderResults({ data });

            if (isInitialPageLoad) {
                isInitialPageLoad = false;
                if (localStorage.getItem('si_offset')) {
                    window.scrollTo(0, parseInt(localStorage.getItem('si_offset')));
                }
            }
        })
        .catch(error => {
            (error.name === 'AbortError') ? console.log('Search aborted') : console.error('Error:', error)
            if (error.name !== 'AbortError') setTimeout(() => document.body.classList.remove('fetching'), 250);
            if (error.name !== 'AbortError') renderResults({ message: 'An error occurred while fetching results. Please try refreshing your browser.' });
        });
}


/**
 * Reset all filters to their default values
 * @returns {void}
 */
const resetFilters = () => {

    // Reset all filters to default our previous values
    updateChoice(industry_select, []);
    updateChoice(providers_select, []);
    updateChoice(status_select, ['open']);
    updateChoice(total_value_select, 'any');
    updateChoice(maximum_fees_select, 'any');
    updateChoice(minimum_investment_select, '');
    updateChoice(minimum_past_return_select, 'any');
    updateChoice(minimum_risk_indicator_select, '1');
    updateChoice(maximum_risk_indicator_select, '7');

    updateInputs('lifeStageFunds', 'Included', true);
    updateInputs('singleClassFunds', 'Included', true);

    // Reset the current page
    current_page = undefined;

    // Reset our default variables
    statusHasChanged = false;
    maxFeesHasChanged = false;
    minPastReturnHasChanged = false;
    minTotalValueHasChanged = false;
    riskIndicatorMinHasChanged = false;
    riskIndicatorMaxHasChanged = false;

}


/**
 * Change the search type (quick or advanced)
 * @param {string} type 
 */
const changeSearchType = (type) => {

    $toggleFilterMode.forEach($item => $item.classList.remove('active'));
    $filterSet.forEach($item => $item.classList.remove('active'));
    $$(`.js-toggle-filter-mode[data-id="${type}"]`).forEach($item => $item.classList.add('active'));
    $$(`.js-filter-set[data-id="${type}"]`).forEach($item => $item.classList.add('active'));
    if (changeSearchTypeHasChanged) resetFilters();



}

$toggleFilterMode.forEach($toggle => {
    // This is here to set the default value for the status filter, as it is required for the API call, and we want to have a default value of 'open' for the quick search
    updateChoice(status_select, ['open']);

    $toggle.addEventListener('click', (e) => {
        e.preventDefault();
        changeSearchTypeHasChanged = true;
        changeSearchType($toggle.dataset.id);
        updateURL();
    });
});

const updateDisclosureTriggerStates = ($triggers, activeId = null) => {
    const triggers = Array.from($triggers);

    // Some trigger sets contain duplicate, hidden controls sharing the same data-id
    // (e.g. the icon-only mix/fees/returns headers) - roving tabindex must only ever
    // land on a real role="tab" element, never one of those duplicates.
    const tabs = triggers.filter($trigger => $trigger.getAttribute('role') === 'tab');

    let $rovingTarget = tabs.find($trigger => $trigger.dataset.id === activeId);
    if (!$rovingTarget) $rovingTarget = tabs.includes(document.activeElement) ? document.activeElement : tabs[0];

    triggers.forEach($trigger => {
        const isActive = activeId !== null && $trigger.dataset.id === activeId;

        // Only triggers that actually disclose something declare aria-expanded in markup
        if ($trigger.hasAttribute('aria-expanded')) {
            $trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        }

        if ($trigger.getAttribute('role') === 'tab') {
            $trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
            $trigger.setAttribute('tabindex', $trigger === $rovingTarget ? '0' : '-1');
        }
    });
}

/**
 * Arrow key / Home / End navigation for ARIA tabs (roving tabindex, manual activation).
 * Moving focus only moves the roving tabindex - it does not select a tab. Activation
 * happens on Enter/Space (native <button> behaviour fires a click for those, so no extra
 * handling is needed here) or on click, both of which run the existing trigger handlers.
 */
const updateRovingTabindex = ($tabs, $focusedTab) => {
    $tabs.forEach($tab => $tab.setAttribute('tabindex', $tab === $focusedTab ? '0' : '-1'));
};

/**
 * Find the other tabs in a given tab's group. Usually that's every role="tab" inside its
 * nearest role="tablist" ancestor - but the card details tabs live inside a flex grid whose
 * item order is reshuffled per breakpoint, so their tablist is a detached element that owns
 * them via aria-owns instead of DOM nesting. Support both.
 */
const getTabGroup = ($tab) => {
    const $tablist = $tab.closest('[role="tablist"]');

    if ($tablist) return Array.from($tablist.querySelectorAll('[role="tab"]'));

    if (!$tab.id) return [];

    const $owner = document.querySelector(`[role="tablist"][aria-owns~="${$tab.id}"]`);

    if (!$owner) return [];

    return ($owner.getAttribute('aria-owns') || '')
        .split(/\s+/)
        .map(id => document.getElementById(id))
        .filter(Boolean);
};

document.body.addEventListener('keydown', (e) => {

    const $tab = e.target.closest('[role="tab"]');

    if (!$tab) return;

    const $tabs = getTabGroup($tab);
    const currentIndex = $tabs.indexOf($tab);

    if (currentIndex === -1) return;

    let newIndex = null;

    switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            newIndex = (currentIndex + 1) % $tabs.length;
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            newIndex = (currentIndex - 1 + $tabs.length) % $tabs.length;
            break;
        case 'Home':
            newIndex = 0;
            break;
        case 'End':
            newIndex = $tabs.length - 1;
            break;
        default:
            return;
    }

    e.preventDefault();
    updateRovingTabindex($tabs, $tabs[newIndex]);
    $tabs[newIndex].focus();

});

/**
 * Escape closes an open tab panel and returns focus to its trigger,
 * mirroring the existing close-button behaviour for the keyboard.
 */
document.body.addEventListener('keydown', (e) => {

    if (e.key !== 'Escape') return;

    // Scope Escape to whichever widget the keypress actually originated in, so closing
    // a panel in one card (or the fund-type filter) can't reach into an unrelated one.
    const $fundTypeContainer = e.target.closest('.radio-tabs');

    if ($fundTypeContainer) {
        const $activeFundTypeTrigger = Array.from($triggerFundTypeDescription).find($trigger => $trigger.classList.contains('active'));

        if ($activeFundTypeTrigger) {
            $activeFundTypeTrigger.classList.remove('active');
            updateDisclosureTriggerStates($triggerFundTypeDescription);
            $fundTypeDescription.forEach($description => $description.classList.remove('active'));
            $activeFundTypeTrigger.focus();
        }

        return;
    }

    const $card = e.target.closest('.card');
    const $activeCardTrigger = $card?.querySelector('.js-trigger-card-details.active');

    if ($activeCardTrigger) {
        const $triggerCardDetails = $card.querySelectorAll('.js-trigger-card-details');
        const $cardDetails = $card.querySelectorAll('.js-card-details');
        $activeCardTrigger.classList.remove('active');
        updateDisclosureTriggerStates($triggerCardDetails);
        $cardDetails.forEach($description => $description.classList.remove('active'));
        $activeCardTrigger.focus();
    }

});

document.body.addEventListener('click', (e) => {

    const $detailsTrigger = e.target.closest('.js-trigger-card-details');

    if ($detailsTrigger) {

        const $card = e.target.closest('.card');
        const $triggerCardDetails = $card.querySelectorAll('.js-trigger-card-details');
        const $cardDetails = $card.querySelectorAll('.js-card-details');

        e.preventDefault();
        if ($detailsTrigger.classList.contains('active')) {
            $detailsTrigger.classList.remove('active');
            updateDisclosureTriggerStates($triggerCardDetails);
            $cardDetails.forEach($cardDetails => {
                $cardDetails.classList.remove('active');
            });
        } else {
            $triggerCardDetails.forEach($description => {
                $description.classList.remove('active');
            });
            $detailsTrigger.classList.add('active');

            let id = $detailsTrigger.dataset.id;
            updateDisclosureTriggerStates($triggerCardDetails, id);
            $cardDetails.forEach($description => {
                if ($description.dataset.id === id) {
                    $description.classList.add('active');
                } else {
                    $description.classList.remove('active');
                }
            });
            prepareScrollableRegions($card);
        }
    }

    const $toggleCompareFund = e.target.closest('.js-toggle-compare-fund');
    if ($toggleCompareFund && $toggleCompareFund.closest('.results')) {
        toggleCompareItem($toggleCompareFund.closest('.card'), $toggleCompareFund);
    }

    const $removeComparedFund = e.target.closest('.js-remove-compared-fund');

    if ($removeComparedFund) {
        let $card = $removeComparedFund.closest('.card');
        removeCompareItem($card.dataset.id, $card, true);
    }

    const $rememberScrollPosition = e.target.closest('.js-remember-scroll-position');

    if ($rememberScrollPosition) {
        localStorage.setItem('si_offset', parseInt(window.scrollY));
    }

});




let toggleCompareItem = ($card, $checkbox) => {

    if ($checkbox.checked) {
        $card.classList.add('active');
        addCompareItem($checkbox.id);
    } else {
        $card.classList.remove('active');
        removeCompareItem($checkbox.id, $card);
    }

};

let sanitisesd = (html, id, prefix = "compare_") => {
    const pattern = /<div class="field checkbox checkbox--compare">[\s\S]*?<\/div>/gi;

    const replacement = /*html*/`
        <div class="field checkbox checkbox--compared [ js-remove-compared-fund ]">
            <input type="checkbox" checked>
            <label class="checkbox__label">
                <span>Remove from compare list</span>
            </label>
        </div>`;

    let output = html.replace(pattern, replacement);

    // The same card markup (and its element ids) gets rendered a second time here when a
    // fund is both in the results list and the compare tray - suffix the tab/panel ids so
    // aria-controls/aria-labelledby/aria-owns stay unambiguous instead of pointing at two
    // elements. IDs appear either quote-terminated (id="...", aria-controls="...") or
    // space-separated (aria-owns="id1 id2 id3"), so match either boundary.
    ['mix', 'fees', 'returns'].forEach(section => {
        ['tab', 'panel'].forEach(kind => {
            const pattern = new RegExp(`fund-${section}-${kind}-${id}(?=["\\s])`, 'g');
            output = output.replace(pattern, `fund-${section}-${kind}-${id}-compare`);
        });
    });

    return output;

}

let updateComparissons = (update = true) => {

    let $compare = $('.js-compare');
    $compare.innerHTML = '';

    if (window.compare_items.length) {
        window.compare_items.forEach(item => {
            $compare.insertAdjacentHTML('beforeend', sanitisesd(item.html, item.id));
        });

        prepareScrollableRegions($compare);

        $$('.js-compare .card').forEach($card => {
            $card.classList.add('active');
        });

        if (update) {
            $$('.js-compare .js-inline-doughnut').forEach($chart => initDoughnutChart($chart));
            $$('.js-compare .js-inline-horizontal-bar-chart').forEach($chart => initBarChart($chart));
            $$('.js-compare .js-inline-line-chart').forEach($chart => initLineChart($chart));
        }

        $$(`.js-toggle-compare-fund`).forEach($checkbox => {
            if (window.compare_items.find(item => item.id === $checkbox.id)) {
                $checkbox.checked = true;
                $checkbox.closest('.card').classList.add('active');
            }
        });
        $compare.classList.add('active');
        document.body.classList.add('has-comparrisons');

        $('.js-compare-bar span').innerText = `View compare list (${window.compare_items.length})`;

        $('.js-compare-count').innerText = `(${window.compare_items.length})`;

    } else {
        $compare.classList.remove('active');
        document.body.classList.remove('has-comparrisons');
    }

    $$('.js-results .card').forEach($card => {
        $card.classList.remove('active');
        $card.querySelector('.js-toggle-compare-fund').checked = false;
    });

    window.compare_items.forEach(item => {
        let $checkbox = $(`.js-toggle-compare-fund[id="${item.id}"]`);
        if ($checkbox) {
            $checkbox.checked = true;
            $checkbox.closest('.card').classList.add('active');
        }
    });

    stickyCompareBar();
}


/**
 * Add an item to the compare list
 * @param {string} id 
 * @returns {void}
 */
let addCompareItem = (id) => {

    if (window.compare_items.find(item => item.id === id)) return;


    let html = window.stored_data?.results.find(item => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = item
        return tempDiv.querySelector('.card')?.dataset.id === id
    })

    if (!html) return;

    window.compare_items.push({ id: id, html: html });

    localStorage.setItem(`compare-${window.compare_type}`, JSON.stringify(window.compare_items));

    updateComparissons();

}

/**
 * Remove an item from the compare list 
 * @param {string} id
 * @param {HTMLElement} $card
 * @param {boolean} animate
 * @returns {void}
 */
let removeCompareItem = (id, $card, animate = false) => {
    window.compare_items = window.compare_items.filter(item => item.id !== id);
    localStorage.setItem(`compare-${window.compare_type}`, JSON.stringify(window.compare_items));
    if (animate) {
        up($card).then(() => {
            updateComparissons();
        });
    } else {
        updateComparissons();
    }
}

$scrollToCompare.forEach($button => {
    $button.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({
            top: $('.js-compare').getBoundingClientRect().top + window.scrollY - 180,
            behavior: 'smooth'
        });
    });
});

$scrollToSearch.forEach($button => {
    $button.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({
            top: $('.results').getBoundingClientRect().top + window.scrollY - 60,
            behavior: 'smooth'
        });
    });
});

$clearCompare.forEach($button => {
    $button.addEventListener('click', (e) => {
        e.preventDefault();
        window.compare_items = [];
        localStorage.setItem(`compare-${window.compare_type}`, JSON.stringify(window.compare_items));
        updateComparissons();

    });
});


if ($loadMoreResults) {
    $loadMoreResults.addEventListener('click', (e) => {
        e.preventDefault();
        const remaining = window.stored_data ? window.stored_data.total - ((current_page || 1) * (window.stored_data.results_per_page || 10)) : 10;
        const nextBatch = Math.min(window.stored_data?.results_per_page || 10, remaining);
        $loadMoreResults.innerHTML = `<div class="load-more__spinner spinner"></div><b>Now loading ${nextBatch} more</b>`;

        current_page = current_page ? current_page + 1 : 2;

        let show_updating_message = false;
        updateURL(show_updating_message);
    });
}


$closeFundTypeDescription.forEach($close => {
    $close.addEventListener('click', (e) => {
        e.preventDefault();
        $fundTypeDescription.forEach($description => {
            $description.classList.remove('active');
        });
        $triggerFundTypeDescription.forEach($description => {
            $description.classList.remove('active');
        });
        updateDisclosureTriggerStates($triggerFundTypeDescription);
    });
});


$triggerFundTypeDescription.forEach($trigger => {
    $trigger.addEventListener('click', (e) => {
        e.preventDefault();

        if ($trigger.classList.contains('active')) {
            $trigger.classList.remove('active');
            updateDisclosureTriggerStates($triggerFundTypeDescription);
            $fundTypeDescription.forEach($description => {
                $description.classList.remove('active');
            });
        } else {
            $triggerFundTypeDescription.forEach($description => {
                $description.classList.remove('active');
            });
            $trigger.classList.add('active');

            let id = $trigger.dataset.id;
            updateDisclosureTriggerStates($triggerFundTypeDescription, id);
            $fundTypeDescription.forEach($description => {
                if ($description.dataset.id === id) {
                    $description.classList.add('active');
                } else {
                    $description.classList.remove('active');
                }
            });
        }

    });
});



/**
 * 
 * Initialise a select item with Choices.js to work with the filters
 *
 * @param {HTMLElement} el
 * @param {string} key
 * @param {function} setFlag
 * @returns {Choices|null}
 */
function initSelect(el, key, setFlag) {

    if (!el) return null;

    // If already initialized, destroy it
    if (el.choicesInstance) {
        el.choicesInstance.destroy();
    }

    const options = el.multiple ? default_multi_choices_options : default_choices_options;
    const selectInstance = new Choices(el, options);

    el.addEventListener('change', (e) => {
        if (setFlag) setFlag();
        updateInputs(key, e.target.value);
    });

    // Keep reference on element so we can check next time
    el.choicesInstance = selectInstance;

    return selectInstance;
};


/**
 * 
 * Update the pretty value for a given filter option
 * 
 * @param {string} value 
 * @param {string} filter 
 * @returns 
 */
const prettyValue = (value, filter) => {
    const type = window.compare_type || 'managed-funds';

    // Labels that depend on the type
    const typeDependent = {
        'name-asc': { 'managed-funds': 'Fund name (A-Z)', default: 'Investment name (A-Z)' },
        'name-desc': { 'managed-funds': 'Fund name (Z-A)', default: 'Investment name (Z-A)' },
    };

    // Labels that are static
    const staticLabels = {
        'any': 'Any',
        '1': 'Included',
        '0': 'Excluded',
        'kiwisaver': 'KiwiSaver',
        'managed': 'Managed funds',
        'all-mf-types': 'KiwiSaver & Managed Funds',
        'fees-asc': 'Fees (lowest first)',
        'fees-desc': 'Fees (highest first)',
        'scheme-type-asc': 'Scheme type (A-Z)',
        'scheme-type-desc': 'Scheme type (Z-A)',
        'return-asc': 'Return (lowest first)',
        'return-desc': 'Return (highest first)',
        'min-invest-asc': 'Min investment (lowest first)',
        'min-invest-desc': 'Min investment (highest first)',
        'closing-date-earliest': 'Closing date (earliest first)',
        'closing-date-latest': 'Closing date (latest first)',
        'maturity-longest': 'Maturity time (longest first)',
        'maturity-shortest': 'Maturity time (shortest first)',
        'growth-assets-asc': 'Growth assets (lowest first)',
        'growth-assets-desc': 'Growth assets (highest first)',
        'fixed-offer-price-desc': 'Fixed offer price (highest first)',
        'fixed-offer-price-asc': 'Fixed offer price (lowest first)'
    };

    const lowerValue = value.toLowerCase();

    if (typeDependent[lowerValue]) {
        return typeDependent[lowerValue][type] || typeDependent[lowerValue].default;
    }

    if (staticLabels[lowerValue]) {
        return staticLabels[lowerValue];
    }

    // Fallback: capitalise first letter
    return value.charAt(0).toUpperCase() + value.slice(1);
};



/**
 * Update the input fields based on the selected filter and value
 * @param {string} filter
 * @param {string} value
 * @param {boolean} skipHistory
 */
let updateInputs = (filter, value, skipHistory = false) => {
    if (filter === 'minTotalValue') {
        if (total_value_select) total_value_select.setChoiceByValue(value);
    } else if (filter === 'minInvest') {
        if (minimum_investment_select) minimum_investment_select.setChoiceByValue(value);
    } else if (filter === 'riskIndicatorMin') {
        if (minimum_risk_indicator_select) minimum_risk_indicator_select.setChoiceByValue(value);
    } else if (filter === 'riskIndicatorMax') {
        if (maximum_risk_indicator_select) maximum_risk_indicator_select.setChoiceByValue(value);
    } else if (filter === 'maxFees') {
        if (maximum_fees_select) maximum_fees_select.setChoiceByValue(value);
    } else if (filter === 'minPastReturn') {
        if (minimum_past_return_select) minimum_past_return_select.setChoiceByValue(value);
    } else if (filter === 'providers') {
        if (providers_select) providers_select.setChoiceByValue(value);
    } else if (filter === 'industry') {
        if (industry_select) industry_select.setChoiceByValue(value);
    } else if (filter === 'fundStatus') {
        if (status_select) status_select.setChoiceByValue(value);
    } else if (filter === 'keyword') {
        $('.js-refine-query').value = value;
    } else {
        $$(`.js-filter-option[data-id="${filter}"]`).forEach($option => $option.checked = ($option.value === value));
        $$(`.js-filter-option-text[data-id="${filter}"]`).forEach($element => {
            $element.innerText = prettyValue(value, filter);
        });
    }

    if (!skipHistory) updateURL();
}

/**
 * Update the URL with the current filter values
 * @param {object} filters
 */
let updateURL = (show_updating_message = true) => {
    let url = '?';

    // Keyword [<value>]
    let keyword = $('.js-refine-query') ? $('.js-refine-query').value : undefined;
    if (keyword) url += `keyword=${encodeURIComponent(keyword)}&`;

    // Managed fund type [kiwisaver-funds, managed-funds]
    let managedFundTypes = $('.js-filter-option[data-id="managedFundTypes"]:checked');
    if (managedFundTypes) url += `managedFundTypes=${managedFundTypes.value}&`;

    // Fund type [defensive, conservative, balanced, growth, aggressive]
    let fundTypes = $('.js-filter-option[data-id="fundTypes"]:checked');
    if (fundTypes) url += `fundTypes=${fundTypes.value}&`;

    // Life Stage Funds [included, excluded]
    let lifeStageFunds = $('.js-filter-option[data-id="lifeStageFunds"]:checked');
    if (lifeStageFunds) url += `lifeStageFunds=${lifeStageFunds.value}&`;

    // Single Class Funds [included, excluded]
    let singleClassFunds = $('.js-filter-option[data-id="singleClassFunds"]:checked');
    if (singleClassFunds) url += `singleClassFunds=${singleClassFunds.value}&`;

    // Search type [quick, advanced]
    let searchType = $('.js-toggle-filter-mode.active');
    if (searchType) url += `searchType=${searchType.dataset.id}&`;

    // Array of fund statuses
    let fundStatus = $('#status') ? [...$('#status').selectedOptions].map(o => o.value) : [];
    if (fundStatus.length && statusHasChanged) fundStatus.forEach(status => url += `fundStatus[]=${status}&`);

    // Array of providers
    let providers = $('#providers') ? [...$('#providers').selectedOptions].map(o => o.value) : [];
    if (providers.length) providers.forEach(provider => url += `providers[]=${provider}&`);

    // Array of industries
    let industries = $('#industry') ? [...$('#industry').selectedOptions].map(o => o.value) : [];
    if (industries.length) industries.forEach(industry => url += `industry[]=${industry}&`);

    // Maximum fees [any, <value>]
    let maxFees = $('#maximum-fees') ? $('#maximum-fees').value : undefined;
    if (maxFees && maxFeesHasChanged) url += `maxFees=${maxFees}&`;

    // Search type [<value>]
    let riskIndicatorMin = $('#minimum-risk-indicator') ? $('#minimum-risk-indicator').value : undefined;
    if (riskIndicatorMin && riskIndicatorMinHasChanged) url += `riskIndicatorMin=${riskIndicatorMin}&`;

    // Search type [<value>]
    let riskIndicatorMax = $('#maximum-risk-indicator') ? $('#maximum-risk-indicator').value : undefined;
    if (riskIndicatorMax && riskIndicatorMaxHasChanged) url += `riskIndicatorMax=${riskIndicatorMax}&`;

    // Minimum past return [any, <value>]
    let minPastReturn = $('#minimum-past-return') ? $('#minimum-past-return').value : undefined;
    if (minPastReturn && minPastReturnHasChanged) url += `minPastReturn=${minPastReturn}&`;

    // Minimum investment [all, <value> or less]
    let minInvest = $('#minimum-investment') ? $('#minimum-investment').value : undefined;
    if (minInvest) url += `minInvest=${minInvest}&`;

    // Minimum total value [any, at least <value>]
    let minTotalValue = $('#total-value') ? $('#total-value').value : undefined;
    if (minTotalValue && minTotalValueHasChanged) url += `minTotalValue=${minTotalValue}&`;

    // Offset
    if (current_page) url += `page=${current_page}&`;

    // Sort [<value>]
    let sort = $('.js-filter-option[data-id="sort"]:checked');
    if (sort) url += `sort=${sort.value}`;

    // if the last character of the url is '?' or '&', remove it
    if (url.slice(-1) === '?' || url.slice(-1) === '&') url = url.slice(0, -1);

    // Update the history
    history.pushState(null, '', `${window.location.origin}${window.location.pathname}${url}`);

    // Fetch the results based on the page type and selected filters    
    if (window.compare_type !== 'managed-funds' || (managedFundTypes && fundTypes)) {
        fetchResults(show_updating_message);
    }

}


/**
 * Load filters from the URL and update the UI accordingly
 * @returns {void}
 */
function loadFiltersFromURL() {

    let filters = new URLSearchParams(window.location.search);

    // Set flag to prevent history update
    isSyncingFromPopstate = true;

    // Update search type [quick, advanced]
    let searchType = filters.get('searchType');
    if (searchType) changeSearchType(searchType);

    // Keyword [<value>]
    if (filters.has('keyword')) updateInputs('keyword', filters.get('keyword'), true);

    // Status [open, closed-to-new-investors, closed-to-all-investors]
    if (filters.has('fundStatus[]')) statusHasChanged = true, updateInputs('fundStatus', filters.getAll('fundStatus[]'), true);

    // Providers [open, closed-to-new-investors, closed-to-all-investors]
    if (filters.has('providers[]')) updateInputs('providers', filters.getAll('providers[]'), true);

    // Industries [Industries]
    if (filters.has('industry[]')) updateInputs('industry', filters.getAll('industry[]'), true);

    // Managed fund type [kiwisaver-funds, managed-funds]
    if (filters.has('managedFundTypes')) updateInputs('managedFundTypes', filters.get('managedFundTypes'), true);

    // Life Stage Funds [included, excluded]
    if (filters.has('lifeStageFunds')) updateInputs('lifeStageFunds', filters.get('lifeStageFunds'), true);

    // Single Class Funds [included, excluded]
    if (filters.has('singleClassFunds')) updateInputs('singleClassFunds', filters.get('singleClassFunds'), true);

    // Fund type [defensive, conservative, balanced, growth, aggressive]
    if (filters.has('fundTypes')) updateInputs('fundTypes', filters.get('fundTypes'), true);

    // Minimum risk indicator [any, <value>]
    if (filters.has('riskIndicatorMin')) updateInputs('riskIndicatorMin', filters.get('riskIndicatorMin'), true);

    // Maximum risk indicator [any, <value>]
    if (filters.has('riskIndicatorMax')) updateInputs('riskIndicatorMax', filters.get('riskIndicatorMax'), true);

    // Minimum past return [all, <value> or less]
    if (filters.has('minPastReturn')) updateInputs('minPastReturn', filters.get('minPastReturn'), true);

    // Minimum investment [all, <value> or less]
    if (filters.has('minInvest')) updateInputs('minInvest', filters.get('minInvest'), true);

    // Minimum total value [any, at least <value>]
    if (filters.has('minTotalValue')) updateInputs('minTotalValue', filters.get('minTotalValue'), true);

    // Sort order [<value>]
    if (filters.has('sort')) updateInputs('sort', filters.get('sort'), true);

    // Unset flag after UI sync
    isSyncingFromPopstate = false;

}

document.addEventListener('change', e => {

    if (!e.target.matches('.js-filter-option')) return;

    current_page = undefined;

    updateInputs(e.target.dataset.id, e.target.value);

});

// Close the dialog only on a genuine pointer click - not a keyboard-
// synthesized one. Arrow-key navigation between radio options (and
// Space/Enter activation) fires a real 'click' event too, but always with
// detail === 0; an actual mouse/touch click has detail >= 1. This keeps
// the dialog open while browsing/selecting with the keyboard, closing
// only on an explicit click (the Close button and Escape still work as
// separate, dedicated ways to dismiss it via keyboard).
document.addEventListener('click', e => {

    if (e.detail === 0) return;

    if (!e.target.matches('.radio-list__item input, .radio-list__item input:checked + label')) return;

    const dialog = e.target.closest('.dialog-select__dialog');

    if (dialog) window.dialogs?.find(d => d.id === dialog.id)?.close();

});


$('.js-refine-query')?.addEventListener('keydown', debounce((e) => {
    e.preventDefault();

    const ignoredKeys = [
        'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape'
    ];

    if (ignoredKeys.includes(e.key)) return;

    // Relevance is a hidden option that only appears when there is a query present
    if (e.target.value.trim() === '') {
        $('.js-filter-option-relevance').style.display = 'none';
        if ($('#growth-assets-desc')) $('#growth-assets-desc').checked = true;
        updateInputs('sort', 'growth-assets-desc', true);
    } else {
        $('.js-filter-option-relevance').style.display = 'flex';
        $('.js-filter-option-relevance').querySelector('input').checked = true;
        updateInputs('sort', 'relevance', true);
    }

    updateInputs('keyword', e.target.value);



    // if (!isSyncingFromPopstate) fetchResults();
}, 300));


if ($compareBar) {

    if (window.default_sort) updateInputs('sort', window.default_sort, true);

    updateComparissons(false);

    loadFiltersFromURL();

    fetchResults();

    // On scroll event handler
    window.addEventListener('scroll', debounce(scrollHandler, 50));

    // On url change event handler
    window.addEventListener('popstate', loadFiltersFromURL);

}
